import { useState, useCallback } from "react";
import type { FileObjectType } from "@jsq/ds/core";
import {
  useCrmGeneratePresignedUrlsMutation,
  useCreateOnboardingJobStepMutation,
} from "~/graphql/supergraphHooks";
import {
  type DataStudioJobTypeEnum,
  type DataStudioJobSubstateEnum,
  type DataStudioCreateJobInput,
} from "~/graphql/supergraphTypes";
import { uploadFilesToS3 } from "~/utils/s3UploadClient";

export interface CrmImportUploadFilesParams {
  files: FileObjectType[];
  environment: string;
  jobType: DataStudioJobTypeEnum;
  arenaDomain: string;
  arenaUserEmail: string;
}

export interface CrmImportCreateJobParams {
  jobType: DataStudioJobTypeEnum;
  arenaDomain: string;
  arenaName?: string;
  arenaUserEmail: string;
  env: string;
  jobConfig: {
    tools: Array<{
      toolId: string;
      toolType: string;
      options: Array<{
        key: string;
        value: string;
        metadata?: boolean;
      }>;
    }>;
  };
  jobId?: string;
  jobName?: string;
  substate?: DataStudioJobSubstateEnum;
  createdAt?: string;
  toolsToValidate?: string[];
}

export interface CrmImportCreateJobResult {
  success: boolean;
  jobId?: string;
  s3Uri?: string;
  message?: string;
}

export interface CrmImportUploadResult {
  objectKey: string;
  fileName: string;
  s3Url: string;
}

/**
 * A React hook for CRM Import file uploads using presigned URLs.
 * CRM-specific implementation that uses local GraphQL mutations.
 *
 * @returns An object containing:
 * - uploadFilesToS3: Function to upload files to Data Studio S3 bucket
 * - createJob: Function to create/update a Data Studio job
 * - loading: Boolean indicating if any operation is in progress
 */
export default function useCrmImportUpload() {
  const [loading, setLoading] = useState(false);

  const [generatePresignedUrlsMutation] = useCrmGeneratePresignedUrlsMutation();

  const [createJobMutation] = useCreateOnboardingJobStepMutation();

  const uploadFiles = useCallback(
    (params: CrmImportUploadFilesParams, retryCount = 3): Promise<CrmImportUploadResult[]> => {
      setLoading(true);

      const input = {
        environment: params.environment,
        jobType: params.jobType,
        arenaDomain: params.arenaDomain,
        arenaUserEmail: params.arenaUserEmail,
        files: params.files.map((fileObj) => ({
          filename: fileObj.file.name,
          contentType: fileObj.file.type || "application/octet-stream",
          contentLength: fileObj.file.size,
        })),
      };

      return generatePresignedUrlsMutation({
        variables: { input },
      })
        .then((urlResponse) => {
          const result = urlResponse.data?.dataStudioGeneratePresignedUrls;

          if (!result?.success || !result.presignedUrlObjs) {
            const errorMessage =
              result?.errors?.join(", ") || "Failed to generate presigned URLs";
            throw new Error(errorMessage);
          }

          const presignedObjs = result.presignedUrlObjs.map((obj) => ({
            s3Url: obj.s3Url,
            objectKey: obj.objectKey,
            fileName: obj.filename,
          }));

          const filesToUpload = params.files.map((fileObj, index) => ({
            generatedUrlObj: presignedObjs[index],
            file: fileObj.file,
          }));

          return uploadFilesToS3(filesToUpload, retryCount).then(() => {
            // Transform the results to match our interface
            return presignedObjs.map((obj) => ({
              objectKey: obj.objectKey,
              fileName: obj.fileName,
              s3Url: obj.s3Url,
            }));
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [generatePresignedUrlsMutation],
  );

  const createJob = useCallback(
    async (params: CrmImportCreateJobParams): Promise<CrmImportCreateJobResult> => {
      setLoading(true);

      try {
        const input: DataStudioCreateJobInput = {
          jobType: params.jobType,
          arenaDomain: params.arenaDomain,
          arenaName: params.arenaName,
          arenaUserEmail: params.arenaUserEmail,
          env: params.env,
          jobConfig: params.jobConfig,
          jobId: params.jobId,
          jobName: params.jobName,
          substate: params.substate,
          createdAt: params.createdAt,
          toolsToValidate: params.toolsToValidate,
        };

        const response = await createJobMutation({
          variables: { input },
        });

        const result = response.data?.dataStudioCreateJob;

        if (!result?.id) {
          throw new Error("No response data received from create job mutation");
        }

        return {
          success: true,
          jobId: result.id,
          s3Uri: result.s3Uri ?? undefined,
          message: result.message ?? undefined,
        };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
        throw new Error(`CRM Import job creation failed: ${errorMessage}`);
      } finally {
        setLoading(false);
      }
    },
    [createJobMutation],
  );

  return {
    uploadFilesToS3: uploadFiles,
    createJob,
    loading,
  };
}
